import { MCP_PROTOCOL_VERSION } from './protocol';

type ToolInput = Record<string, unknown>;
type ModelContextLike = {
  registerTool(tool: {
    name: string;
    title: string;
    description: string;
    inputSchema: Record<string, unknown>;
    annotations: { readOnlyHint: boolean; consequentialHint: boolean };
    execute(input: ToolInput, options: { signal: AbortSignal }): Promise<unknown>;
  }): Promise<void>;
};

type ModelContextNavigator = Navigator & { modelContext?: ModelContextLike };
type ModelContextDocument = Document & { modelContext?: ModelContextLike };

let rpcId = 0;
let negotiatedProtocol: string | null = null;
let initialization: Promise<void> | null = null;

function modelContext(): ModelContextLike | undefined {
  // Chrome's current preview and the WebMCP draft have used navigator and document
  // attachment points respectively; supporting both is feature detection, not a polyfill.
  return (navigator as ModelContextNavigator).modelContext ?? (document as ModelContextDocument).modelContext;
}

async function postRpc(method: string, params: ToolInput, signal?: AbortSignal): Promise<Record<string, unknown>> {
  const headers = new Headers({
    Accept: 'application/json, text/event-stream',
    'Content-Type': 'application/json',
  });
  if (negotiatedProtocol && method !== 'initialize') headers.set('MCP-Protocol-Version', negotiatedProtocol);
  const response = await fetch('/mcp', {
    method: 'POST',
    headers,
    body: JSON.stringify({ jsonrpc: '2.0', id: ++rpcId, method, params }),
    credentials: 'omit',
    signal,
  });
  const body = await response.json() as { result?: Record<string, unknown>; error?: { message?: string } };
  if (!response.ok || body.error || !body.result) throw new Error(body.error?.message ?? `MCP request failed with HTTP ${response.status}`);
  return body.result;
}

async function initialize(): Promise<void> {
  if (!initialization) {
    initialization = postRpc('initialize', {
      protocolVersion: MCP_PROTOCOL_VERSION,
      capabilities: {},
      clientInfo: { name: 'bottomsurgery.info-webmcp-bridge', version: '1.0.0' },
    }).then((result) => {
      if (typeof result.protocolVersion !== 'string') throw new Error('MCP initialize response has no protocolVersion');
      negotiatedProtocol = result.protocolVersion;
    }).catch((error) => {
      initialization = null;
      throw error;
    });
  }
  return initialization;
}

async function callPublicTool(name: string, input: ToolInput, signal: AbortSignal): Promise<unknown> {
  await initialize();
  const result = await postRpc('tools/call', { name, arguments: input }, signal);
  if (result.isError === true) {
    const text = Array.isArray(result.content) ? result.content[0] : undefined;
    throw new Error(text && typeof text === 'object' && 'text' in text && typeof text.text === 'string' ? text.text : 'MCP tool request failed');
  }
  return result.structuredContent ?? result.content;
}

const readOnly = { readOnlyHint: true, consequentialHint: false };

export async function registerWebMcpTools(): Promise<boolean> {
  const context = modelContext();
  if (!context) return false;
  await Promise.all([
    context.registerTool({
      name: 'bottomsurgery_list_topics',
      title: 'List public guide topics',
      description: 'List public bottomsurgery.info guide, procedure, collection, and tool topics in English or Spanish. Results are paginated.',
      inputSchema: {
        type: 'object', additionalProperties: false,
        properties: {
          language: { type: 'string', enum: ['en', 'es'] },
          category: { type: 'string' },
          limit: { type: 'integer', minimum: 1, maximum: 25 },
          cursor: { type: 'string' },
        },
      },
      annotations: readOnly,
      execute: (input, options) => callPublicTool('bottomsurgery_list_topics', input, options.signal),
    }),
    context.registerTool({
      name: 'bottomsurgery_search_topics',
      title: 'Search public guide topics',
      description: 'Search the public bottomsurgery.info EN or ES topical corpus and return exact excerpts with canonical routes.',
      inputSchema: {
        type: 'object', additionalProperties: false, required: ['query'],
        properties: {
          query: { type: 'string', minLength: 1, maxLength: 160 },
          language: { type: 'string', enum: ['en', 'es'] },
          limit: { type: 'integer', minimum: 1, maximum: 25 },
          cursor: { type: 'string' },
        },
      },
      annotations: readOnly,
      execute: (input, options) => callPublicTool('bottomsurgery_search_topics', input, options.signal),
    }),
    context.registerTool({
      name: 'bottomsurgery_read_topic',
      title: 'Read a public guide topic',
      description: 'Read complete public text for an exact canonical topic route returned by the list or search tools.',
      inputSchema: {
        type: 'object', additionalProperties: false, required: ['route'],
        properties: { route: { type: 'string' }, language: { type: 'string', enum: ['en', 'es'] } },
      },
      annotations: readOnly,
      execute: (input, options) => callPublicTool('bottomsurgery_read_topic', input, options.signal),
    }),
  ]);
  return true;
}
